import { getRequestStatusLabel, isOpenRequest } from "./requestHelpers";

function filterMyRequests(pendingRequests, user) {
  const userId = user?.id;
  const userCode = user?.code;
  const employeeId = user?.employeeId;

  return (pendingRequests || []).filter(
    (r) =>
      r.userId === userId ||
      r.employeeId === employeeId ||
      r.employeeId === userId ||
      (userCode && r.employeeCode === userCode),
  );
}

function isLeaveRequestType(type) {
  return (
    type?.includes("إجازة") || type === "إذن" || type?.includes("مرضية")
  );
}

export function resolveMyEmployeeRecord(allEmployees, user) {
  if (!user) return null;

  return (
    allEmployees.find((e) => e.id === user.employeeId) ||
    allEmployees.find((e) => e.code === user.code) ||
    allEmployees.find((e) => e.userId === user.id) ||
    null
  );
}

/** Own non-cancelled penalties for home / appeal UI. */
export function filterMyPenalties(penalties, employeeId) {
  if (!employeeId) return [];
  return (penalties || []).filter(
    (pen) =>
      String(pen.employeeId) === String(employeeId) &&
      pen.status !== "cancelled",
  );
}

export function buildLeaveBalanceSummary(employee, pendingRequests, user) {
  const leaveBalance = employee?.leaveBalance || {};
  const total = leaveBalance.annualEntitlement ?? 8;
  const used = leaveBalance.annualUsed ?? 0;
  const remaining =
    leaveBalance.annualRemaining ?? Math.max(0, total - used);
  const casualTotal = leaveBalance.casualEntitlement ?? 0;
  const casualUsed = leaveBalance.casualUsed ?? 0;
  const casualRemaining =
    leaveBalance.casualRemaining ?? Math.max(0, casualTotal - casualUsed);
  const pending = filterMyRequests(pendingRequests, user).filter(
    (r) => isOpenRequest(r) && isLeaveRequestType(r.type),
  ).length;

  return {
    total,
    used,
    remaining,
    casualTotal,
    casualUsed,
    casualRemaining,
    pending,
  };
}

export function buildAdvanceSummary(employee, pendingRequests, user) {
  const advance = employee?.advance || {};
  const ordinary = advance.ordinary || {};
  const exceptional = advance.exceptional || {};

  const ordinaryOutstanding = Number(ordinary.outstandingAmount) || 0;
  const exceptionalOutstanding = Number(exceptional.outstandingAmount) || 0;
  const exceptionalTotal = Number(exceptional.installmentsTotal) || 0;
  const exceptionalPaid = Number(exceptional.installmentsPaid) || 0;
  const exceptionalRemainingInstallments = Math.max(
    0,
    exceptionalTotal - exceptionalPaid,
  );
  const exceptionalMonthly =
    exceptionalRemainingInstallments > 0
      ? Math.round(
          ((exceptionalOutstanding / exceptionalRemainingInstallments) +
            Number.EPSILON) *
            100,
        ) / 100
      : exceptionalTotal > 0 && exceptionalOutstanding > 0
        ? Math.round(
            ((exceptionalOutstanding / exceptionalTotal) + Number.EPSILON) *
              100,
          ) / 100
        : 0;

  const outstanding =
    Number(advance.outstandingAmount) ||
    ordinaryOutstanding + exceptionalOutstanding;
  const installmentsTotal =
    Number(advance.installmentsTotal) ||
    (Number(ordinary.installmentsTotal) || 0) + exceptionalTotal;
  const installmentsPaid =
    Number(advance.installmentsPaid) ||
    (Number(ordinary.installmentsPaid) || 0) + exceptionalPaid;
  const installmentsRemaining = Math.max(
    0,
    installmentsTotal - installmentsPaid,
  );
  const pendingRequest = filterMyRequests(pendingRequests, user).find(
    (r) =>
      isOpenRequest(r) && (r.type === "سلفة" || r.type === "advance"),
  );

  return {
    outstanding,
    installmentsTotal,
    installmentsPaid,
    installmentsRemaining,
    pendingRequest,
    ordinary: {
      outstanding: ordinaryOutstanding,
    },
    exceptional: {
      outstanding: exceptionalOutstanding,
      installmentsTotal: exceptionalTotal,
      installmentsPaid: exceptionalPaid,
      installmentsRemaining: exceptionalRemainingInstallments,
      monthlyInstallment: exceptionalMonthly,
    },
  };
}

/** Ordinary + exceptional advance cards for employee / manager / DM home. */
export function buildAdvanceStatCards(advance) {
  return [
    {
      id: "advance-ordinary",
      label: "سلفة عادية",
      value:
        advance.ordinary.outstanding > 0
          ? `${advance.ordinary.outstanding} ج.م`
          : "—",
      note:
        advance.pendingRequest?.amount != null &&
        Number(advance.pendingRequest.installments || 1) <= 1
          ? `طلب جديد: ${advance.pendingRequest.amount} ج.م`
          : advance.ordinary.outstanding > 0
            ? "تُخصم تلقائياً من مرتب الشهر التالي"
            : "لا توجد سلفة عادية",
    },
    {
      id: "advance-exceptional",
      label: "سلفة استثنائية",
      value:
        advance.exceptional.outstanding > 0
          ? `${advance.exceptional.outstanding} ج.م`
          : "—",
      note:
        advance.pendingRequest?.amount != null &&
        Number(advance.pendingRequest.installments || 1) > 1
          ? `طلب جديد: ${advance.pendingRequest.amount} ج.م`
          : advance.exceptional.installmentsRemaining > 0
            ? `متبقي ${advance.exceptional.installmentsRemaining} قسط × ${advance.exceptional.monthlyInstallment} ج.م — يُخصم أول يوم في الشهر التالي`
            : advance.exceptional.installmentsTotal > 0
              ? `${advance.exceptional.installmentsPaid}/${advance.exceptional.installmentsTotal} قسط مدفوع`
              : "لا توجد سلفة استثنائية",
    },
  ];
}

function statusUiKey(status) {
  if (status === "approved") return "approved";
  if (status === "rejected" || status === "manager_rejected" || status === "dm_rejected") {
    return "rejected";
  }
  return "review";
}

function requestSortKey(request) {
  return String(request.submittedAt || request.createdAt || "");
}

function homeRequestPriority(request) {
  if (isOpenRequest(request)) return 0;
  const key = statusUiKey(request.status);
  if (key === "rejected") return 1;
  if (key === "approved") return 2;
  return 3;
}

function mapHomeRequestRow(request) {
  return {
    id: request.id,
    type: request.type,
    date: request.submittedAt,
    startDate: request.startDate,
    endDate: request.endDate,
    days: request.days,
    status: statusUiKey(request.status),
    statusLabel: getRequestStatusLabel(request.status),
    source: request,
  };
}

/** Home feed: open first, then rejected, then recent approved. */
export function buildHomeMyRequests(pendingRequests, user, limit = 5) {
  return filterMyRequests(pendingRequests, user)
    .slice()
    .sort((a, b) => {
      const byPriority = homeRequestPriority(a) - homeRequestPriority(b);
      if (byPriority !== 0) return byPriority;
      return requestSortKey(b).localeCompare(requestSortKey(a));
    })
    .slice(0, limit)
    .map(mapHomeRequestRow);
}

/** Approved leave that is ongoing or starts today/later — not a second request list. */
export function buildUpcomingApprovedLeaves(
  pendingRequests,
  user,
  todayIso = new Date().toISOString().slice(0, 10),
  limit = 3,
) {
  return filterMyRequests(pendingRequests, user)
    .filter((r) => {
      if (statusUiKey(r.status) !== "approved") return false;
      if (r._source === "permit" || r.type === "إذن" || r.type === "إذن تعويضي") {
        return false;
      }
      if (
        !(
          r._source === "leave" ||
          r.type?.includes("إجازة") ||
          r.type?.includes("مرضية")
        )
      ) {
        return false;
      }
      const start = String(r.startDate || "").slice(0, 10);
      const end = String(r.endDate || start).slice(0, 10);
      if (!start) return false;
      return end >= todayIso;
    })
    .sort((a, b) =>
      String(a.startDate || "").localeCompare(String(b.startDate || "")),
    )
    .slice(0, limit)
    .map((r) => {
      const start = String(r.startDate || "").slice(0, 10);
      const end = String(r.endDate || start).slice(0, 10);
      const ongoing = start <= todayIso && end >= todayIso;
      return {
        id: r.id,
        type: r.type,
        startDate: start,
        endDate: end,
        days: r.days,
        status: "approved",
        statusLabel: ongoing ? "جارية الآن" : "قادمة",
        ongoing,
        note: ongoing
          ? end === todayIso
            ? "تنتهي اليوم"
            : `جارية حتى ${end}`
          : start === todayIso
            ? "تبدأ اليوم"
            : `هتبدأ يوم ${start}`,
      };
    });
}

export function buildRecentLeaveRequests(pendingRequests, user, limit = 5) {
  return buildUpcomingApprovedLeaves(pendingRequests, user, undefined, limit);
}

export function buildRecentRequests(pendingRequests, user, limit = 5) {
  return buildHomeMyRequests(pendingRequests, user, limit);
}

export function buildEmployeeHomeStats({
  employee,
  pendingRequests,
  recentActivities,
  user,
}) {
  const leave = buildLeaveBalanceSummary(employee, pendingRequests, user);
  const advance = buildAdvanceSummary(employee, pendingRequests, user);
  const myPendingCount = filterMyRequests(pendingRequests, user).filter((r) =>
    isOpenRequest(r),
  ).length;

  return [
    {
      id: "leave-remaining",
      label: "رصيد الإجازات",
      value: String(leave.remaining),
      note: `من ${leave.total} يوم (مستخدم ${leave.used})`,
    },
    {
      id: "casual-remaining",
      label: "إجازة عارضة",
      value: String(leave.casualRemaining),
      note: `من ${leave.casualTotal} يوم (مستخدم ${leave.casualUsed})`,
    },
    {
      id: "leave-used",
      label: "إجازات مستخدمة",
      value: String(leave.used),
      note: `السنة الحالية ${new Date().getFullYear()}`,
    },
    {
      id: "leave-pending",
      label: "طلبات إجازة معلقة",
      value: String(leave.pending),
      note: leave.pending ? "بانتظار الموافقة" : "لا طلبات معلقة",
    },
    ...buildAdvanceStatCards(advance),
    {
      id: "pending",
      label: "طلبات معلقة",
      value: String(myPendingCount),
      note: myPendingCount ? "بانتظار الرد" : "لا طلبات معلقة",
    },
    {
      id: "activities",
      label: "آخر الأنشطة",
      value: String(recentActivities.length),
      note: recentActivities.length ? "نشاط مسجل في الجلسة" : "لا أنشطة مسجلة",
    },
  ];
}

export function buildDepartmentManagerHomeStats({
  employee,
  pendingRequests,
  managerPenalties,
  user,
}) {
  const leave = buildLeaveBalanceSummary(employee, pendingRequests, user);
  const advance = buildAdvanceSummary(employee, pendingRequests, user);
  const openPenalties = managerPenalties.filter((p) => p.status === "active");
  const myPendingCount = filterMyRequests(pendingRequests, user).filter((r) =>
    isOpenRequest(r),
  ).length;

  return [
    {
      id: "leave-remaining",
      label: "رصيد الإجازات",
      value: String(leave.remaining),
      note: `من ${leave.total} يوم (مستخدم ${leave.used})`,
    },
    {
      id: "casual-remaining",
      label: "إجازة عارضة",
      value: String(leave.casualRemaining),
      note: `من ${leave.casualTotal} يوم (مستخدم ${leave.casualUsed})`,
    },
    {
      id: "leave-used",
      label: "إجازات مستخدمة",
      value: String(leave.used),
      note: `السنة الحالية ${new Date().getFullYear()}`,
    },
    {
      id: "leave-pending",
      label: "طلبات إجازة معلقة",
      value: String(leave.pending),
      note: leave.pending ? "بانتظار الموافقة" : "لا طلبات معلقة",
    },
    ...buildAdvanceStatCards(advance),
    {
      id: "pending-requests",
      label: "طلباتي المعلقة",
      value: String(myPendingCount),
      note: myPendingCount ? "بانتظار الرد" : "لا طلبات معلقة",
    },
    {
      id: "penalties",
      label: "الجزاءات الحالية",
      value: String(openPenalties.length),
      note: openPenalties.length ? "جزاء ساري" : "لا جزاءات",
    },
  ];
}

export function buildManagerHomeStats({
  employee,
  pendingRequests,
  managerPenalties,
  user,
}) {
  const leave = buildLeaveBalanceSummary(employee, pendingRequests, user);
  const advance = buildAdvanceSummary(employee, pendingRequests, user);
  const myPendingCount = filterMyRequests(pendingRequests, user).filter((r) =>
    isOpenRequest(r),
  ).length;
  const openPenalties = managerPenalties.filter((p) => p.status === "active");
  const teamPendingCount = pendingRequests.filter(
    (r) =>
      r.status === "pending" &&
      r.userId !== user?.id &&
      r.employeeId !== user?.employeeId,
  ).length;

  return [
    {
      id: "leave-remaining",
      label: "رصيد الإجازات",
      value: String(leave.remaining),
      note: `من ${leave.total} يوم (مستخدم ${leave.used})`,
    },
    {
      id: "casual-remaining",
      label: "إجازة عارضة",
      value: String(leave.casualRemaining),
      note: `من ${leave.casualTotal} يوم (مستخدم ${leave.casualUsed})`,
    },
    {
      id: "leave-used",
      label: "إجازات مستخدمة",
      value: String(leave.used),
      note: `السنة الحالية ${new Date().getFullYear()}`,
    },
    {
      id: "leave-pending",
      label: "طلبات إجازة معلقة",
      value: String(leave.pending),
      note: leave.pending ? "بانتظار الموافقة" : "لا طلبات معلقة",
    },
    ...buildAdvanceStatCards(advance),
    {
      id: "pending-requests",
      label: "طلباتي المعلقة",
      value: String(myPendingCount),
      note: myPendingCount ? "بانتظار الموافقة" : "لا طلبات معلقة",
    },
    {
      id: "penalties",
      label: "الجزاءات الحالية",
      value: String(openPenalties.length),
      note: openPenalties.length ? "جزاء ساري" : "لا جزاءات",
    },
    {
      id: "team",
      label: "طلبات الفريق",
      value: String(teamPendingCount),
      note: "بانتظار مراجعتك",
    },
  ];
}
